/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import MainNavigation from "@/components/layout/main-navigation";
import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { MarketingSectionHeader } from "@/components/landing/marketing-section-header";
import { FadeIn } from "@/components/landing/primitives/fade-in";
import {
  BookOpen,
  Download,
  ExternalLink,
  FileText,
  MessageCircle,
  Play,
  type LucideIcon,
} from "lucide-react";
import { EMAILS } from "@/lib/constants/master";

interface ResourceItem {
  title: string;
  description: string;
  icon: string;
  type: string;
  link: string;
  external: boolean;
}

const iconMap: Record<string, LucideIcon> = {
  FileText,
  Play,
  BookOpen,
  Download,
  MessageCircle,
};

interface ResourceCategory {
  category: string;
  items: ResourceItem[];
}

interface FeaturedContent {
  title: string;
  description: string;
  image: string;
  readTime: string;
  category: string;
  link?: string;
}

interface ResourcesPageClientProps {
  resources: ResourceCategory[];
  featuredContent: FeaturedContent[];
}

export function ResourcesPageClient({
  resources,
  featuredContent,
}: ResourcesPageClientProps) {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      <MarketingPageHero
        variant="dark"
        badge="Resources"
        title={
          <>
            Everything you need to{" "}
            <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
              succeed
            </span>
          </>
        }
        description="Guides, documentation, and tools to help you master estimation and get the most out of Contractor Co-Pilot."
      />

      {/* Featured Content */}
      <section
        className="py-16 md:py-24 lg:py-32 bg-background"
        aria-labelledby="featured-content"
      >
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader
            badge="Featured"
            title="Get started quickly"
            description="Our most popular guides to help you hit the ground running."
          />

          <div className="grid gap-6 md:grid-cols-3">
            {featuredContent.map((content, index) => (
              <FadeIn key={content.title} delay={index * 100}>
                <a
                  href={content.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-premium group rounded-2xl border border-border bg-muted/30 overflow-hidden block h-full"
                >
                  <div className="relative h-40 bg-primary/5">
                    <div className="absolute left-4 top-4">
                      <span className="rounded-full bg-secondary/10 text-secondary px-3 py-1 text-xs font-medium">
                        {content.category}
                      </span>
                    </div>
                    <div className="absolute right-4 top-4">
                      <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="mb-2 text-lg font-bold text-foreground font-display group-hover:text-secondary transition-colors">
                      {content.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                      {content.description}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {content.readTime}
                    </span>
                  </div>
                </a>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Resources by Category */}
      <section
        className="py-16 md:py-24 lg:py-32 bg-background-warm"
        aria-labelledby="browse-by-category"
      >
        <div className="mx-auto max-w-[1280px] px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader
            title="Browse by category"
            description="Find exactly what you need across our documentation and guides."
          />

          <div className="space-y-12">
            {resources.map((category) => (
              <div key={category.category}>
                <h3 className="mb-6 text-lg font-bold text-foreground font-display">
                  {category.category}
                </h3>
                <div className="grid gap-4 md:grid-cols-3">
                  {category.items.map((item, index) => {
                    const IconComponent = iconMap[item.icon] ?? FileText;
                    return (
                      <FadeIn key={item.title} delay={index * 100}>
                        <a
                          href={item.link}
                          target={item.external ? "_blank" : undefined}
                          rel={
                            item.external ? "noopener noreferrer" : undefined
                          }
                          className="card-premium group flex items-start gap-4 rounded-2xl border border-border bg-background p-5 h-full"
                        >
                          <div
                            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-secondary"
                            aria-hidden="true"
                          >
                            <IconComponent className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold text-foreground font-display group-hover:text-secondary transition-colors">
                                {item.title}
                              </h4>
                              {item.external && (
                                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {item.description}
                            </p>
                            <span className="inline-block mt-2 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                              {item.type}
                            </span>
                          </div>
                        </a>
                      </FadeIn>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketingCTASection
        title="Need more help?"
        description="Our support team is here to help you get the most out of Contractor Co-Pilot."
        primaryCTA={{
          text: "Contact Support",
          href: `mailto:${EMAILS.CONTACT}`,
        }}
        secondaryCTA={{ text: "Start Your Free Trial", href: "/auth/sign-up" }}
        trustIndicators={["We respond within 24 hours"]}
      />

      <Footer />
    </div>
  );
}
